import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import {
  addBudgetSalesItem,
  deleteBudgetSalesItem,
  getBudgetCatalog,
  updateBudgetSalesItem,
} from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';
import { PartyFields, PartyHint } from './budgetPartyFields';

const LINE_LABELS = { tcoat: 'T-COAT', upselling: 'Upselling' };
const CHANNEL_OPTIONS = [
  { code: 'B2B', label: 'B2B' },
  { code: 'B2G', label: 'B2G' },
  { code: 'B2B2C', label: 'B2B2C' },
];

const monthKey = (year, month) => `${Number(year)}-${String(Number(month) || 1).padStart(2, '0')}`;

const fallbackAxis = (year) =>
  MONTH_LABELS.map((label, index) => ({
    year: Number(year),
    month: index + 1,
    label: `${label} ${year}`,
    short_label: label,
  }));

const parseNumber = (value) => Number(String(value ?? '').replace(',', '.')) || 0;

const emptyQuantities = (count) => Array.from({ length: Math.max(count, 1) }, () => '');

const BudgetVariableSalesPanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    product_sku: '',
    channel: '',
    unit_price: '',
    budget_account_id: '',
    notes: '',
    supplier: '',
    payment_term: 'contado',
    quantities: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);

  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const monthAxis = useMemo(
    () => (Array.isArray(budget?.month_axis) && budget.month_axis.length ? budget.month_axis : fallbackAxis(budgetYear)),
    [budget, budgetYear]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getBudgetCatalog();
        if (!cancelled) setProducts(Array.isArray(payload?.products) ? payload.products : []);
      } catch {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const channels = useMemo(() => {
    const seen = new Set(CHANNEL_OPTIONS.map((row) => row.label));
    const extra = (budget?.sales_items || [])
      .filter((item) => item.schedule_kind === 'variable')
      .map((item) => String(item.channel || '').trim())
      .filter((label) => label && !seen.has(label));
    extra.forEach((label) => seen.add(label));
    return [...CHANNEL_OPTIONS, ...extra.map((label) => ({ code: label, label }))];
  }, [budget]);

  const ingresoAccounts = useMemo(
    () => flattenBudgetAccounts(budgetAccounts).filter((row) => row.nature === 'ingreso'),
    [budgetAccounts]
  );

  const unitPrice = parseNumber(form.unit_price);
  const monthRows = useMemo(
    () =>
      monthAxis.map((col, index) => {
        const quantity = parseNumber(form.quantities[index]);
        return {
          ...col,
          quantity,
          amount: Math.round(quantity * unitPrice * 100) / 100,
        };
      }),
    [monthAxis, form.quantities, unitPrice]
  );
  const totalQty = monthRows.reduce((sum, row) => sum + row.quantity, 0);
  const totalAmount = monthRows.reduce((sum, row) => sum + row.amount, 0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.sales_items || [])
      .filter((item) => item.schedule_kind === 'variable')
      .filter((item) => {
        if (channelFilter !== 'all' && item.channel !== channelFilter) return false;
        if (!q) return true;
        return `${item.product_sku} ${item.product_name} ${item.channel} ${item.account_label} ${item.supplier}`.toLowerCase().includes(q);
      });
  }, [budget, search, channelFilter]);

  const quantitiesFromItem = (item) =>
    monthAxis.map((col, index) => {
      const key = monthKey(col.year, col.month);
      const scheduled = item?.quantity_schedule?.[key];
      if (scheduled != null && scheduled !== '') return String(scheduled);
      const listValue = item?.quantity_months?.[index];
      return listValue ? String(listValue) : '';
    });

  const openCreate = () => {
    setEditing(null);
    setForm({
      product_sku: '',
      channel: '',
      unit_price: '',
      budget_account_id: '',
      notes: '',
      supplier: '',
      payment_term: 'contado',
      quantities: emptyQuantities(monthAxis.length),
    });
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      product_sku: item.product_sku || '',
      channel: item.channel || '',
      unit_price: item.unit_price ? String(item.unit_price) : '',
      budget_account_id: String(item.budget_account_id || ''),
      notes: item.notes || '',
      supplier: item.supplier || '',
      payment_term: item.payment_term || 'contado',
      quantities: quantitiesFromItem(item),
    });
    setError('');
    setShowForm(true);
  };

  const setQuantityAt = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.quantities || [])];
      while (next.length < monthAxis.length) next.push('');
      next[index] = value;
      return { ...prev, quantities: next };
    });
  };

  const payloadFromForm = () => {
    const quantity_months = {};
    monthAxis.forEach((col, index) => {
      const qty = parseNumber(form.quantities[index]);
      if (Math.abs(qty) > 0.00004) quantity_months[monthKey(col.year, col.month)] = qty;
    });
    return {
      product_sku: form.product_sku,
      channel: form.channel,
      unit_price: unitPrice,
      quantity: totalQty,
      amount: totalAmount,
      schedule_kind: 'variable',
      quantity_months,
      recurrence: 'Mensual',
      budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
      notes: form.notes,
      supplier: form.supplier,
      payment_term: form.payment_term || 'contado',
    };
  };

  const handleSave = async () => {
    if (!budget?.id) return;
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const next = editing
        ? await updateBudgetSalesItem(budget.id, editing.id, payload)
        : await addBudgetSalesItem(budget.id, payload);
      setShowForm(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la proyección.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.product_name} · ${item.channel}?`)) return;
    setBusy(true);
    setError('');
    try {
      const next = await deleteBudgetSalesItem(budget.id, item.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la proyección.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Ventas variables</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Precio unitario fijo y cantidades distintas cada mes. El monto mensual es cantidad × precio y suma en Ingresos.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar producto o canal…"
            style={{ ...styles.input, width: 240 }}
          />
          <select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)} style={{ ...styles.input, width: 220 }}>
            <option value="all">Todos los canales</option>
            {channels.map((option) => (
              <option key={option.label} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Agregar venta variable
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              {['Producto', 'Canal', 'Precio', 'Cant. total', 'Monto', 'Cuenta', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: ['Precio', 'Cant. total', 'Monto'].includes(label) ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{item.product_name}</div>
                  <div style={{ fontSize: 11, color: ui.muted }}>{item.product_sku}</div>
                  <PartyHint item={item} ui={ui} />
                  <Pill label={LINE_LABELS[item.product_line] || item.product_line} tone="info" isDarkMode={isDarkMode} />
                </td>
                <td style={{ padding: 8, fontSize: 12, fontWeight: 700 }}>{item.channel}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{item.unit_price ? money(item.unit_price) : '—'}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{item.quantity || '—'}</td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{money(item.budget_total)}</td>
                <td style={{ padding: 8, fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                  {item.account_label || item.account_code || '—'}
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit ? (
                    <>
                      <button type="button" style={styles.ghostBtn} onClick={() => openEdit(item)}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(item)} disabled={busy}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Agrega ventas con precio fijo y una cantidad distinta por cada mes del periodo proyectado.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar venta variable' : 'Nueva venta variable'}
          subtitle="El precio se mantiene fijo. Completa la cantidad de cada mes; el monto se calcula solo."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Producto
            <select
              value={form.product_sku}
              onChange={(event) => setForm((prev) => ({ ...prev, product_sku: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona un producto</option>
              {products.map((option) => (
                <option key={option.product_sku} value={option.product_sku}>
                  {LINE_LABELS[option.product_line] || option.product_line} · {option.product_sku} · {option.product_name}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Canal
            <select value={form.channel} onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))} style={styles.input}>
              <option value="">Selecciona un canal</option>
              {CHANNEL_OPTIONS.map((option) => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
              {form.channel && !CHANNEL_OPTIONS.some((option) => option.label === form.channel) ? (
                <option value={form.channel}>{form.channel}</option>
              ) : null}
            </select>
          </label>
          <label style={styles.label}>
            Precio unitario
            <input value={form.unit_price} onChange={(event) => setForm((prev) => ({ ...prev, unit_price: event.target.value }))} style={styles.input} />
          </label>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: ui.text }}>Cantidad por mes</span>
              <span style={{ fontSize: 12, color: ui.muted }}>
                {totalQty.toLocaleString('es-CL', { maximumFractionDigits: 2 })} un. · {money(totalAmount)}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 8 }}>
              {monthRows.map((row, index) => (
                <label key={`${row.year}-${row.month}`} style={{ ...styles.label, margin: 0 }}>
                  {row.short_label || row.label}
                  <input
                    value={form.quantities[index] ?? ''}
                    onChange={(event) => setQuantityAt(index, event.target.value)}
                    style={styles.input}
                    inputMode="decimal"
                  />
                  <span style={{ fontSize: 11, color: ui.muted }}>{row.quantity ? money(row.amount) : '—'}</span>
                </label>
              ))}
            </div>
          </div>
          <label style={styles.label}>
            Clasificación (cuenta de ingreso)
            <select
              value={form.budget_account_id}
              onChange={(event) => setForm((prev) => ({ ...prev, budget_account_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona una cuenta de ingreso</option>
              {ingresoAccounts.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel}
                </option>
              ))}
            </select>
            {!ingresoAccounts.length ? (
              <span style={{ fontSize: 11, color: ui.muted }}>Crea una cuenta de naturaleza Ingreso en la píldora Cuentas.</span>
            ) : null}
          </label>
          <PartyFields form={form} setForm={setForm} styles={styles} />
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetVariableSalesPanel;
